import { memo, startTransition, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import '../../accounting/accounting.css';
import './glossaryPage.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { FormTitle } from '../../../components/FormTitle';
import { ViewToggle } from '../../../components/ViewToggle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { runLoading } from '../../../components/loading';
import { api } from '../../../api/client';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { useGlossary, errMessage, type GlossaryRow } from './systemMasters';
import { GlossaryDetailView } from './GlossaryDetailView';

interface Props { onHome?: () => void; }

const txt = (v: string | null | undefined) => v ?? '';

/** One Definition cell. It owns its text while the user types, so a keystroke re-renders this one
 *  input instead of the whole 10,000-row page (owner report 2026-10-12: typing lagged). The page is
 *  told the new value through `onEdit`, which only re-renders when the NUMBER of changed rows moves. */
const DefinitionCell = memo(function DefinitionCell({ id, initial, original, onEdit }: {
  id: string; initial: string; original: string; onEdit: (id: string, value: string) => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <input className={`gloss-def-input${value !== original ? ' dirty' : ''}`} aria-label="Definition" value={value}
      onChange={(e) => { setValue(e.target.value); onEdit(id, e.target.value); }} onClick={(e) => e.stopPropagation()} />
  );
});

/**
 * Settings ▸ System Management ▸ Glossary — LIMS Glossary_ListView.
 * Read-only list of every glossary field (FieldID / FieldName / Acronym / Definition /
 * TableName / Module / BO) with ONLY the Definition column editable inline (LIMS
 * InlineEditMode="Batch"): edits accumulate, then "Save changes" commits them all and
 * "Cancel changes" discards. No New / Delete (AllowNew/AllowDelete=False). Sorted by FieldID.
 * A record opens in the full-page Detail View (owner request 2026-10-15, G5 parity — reverses the 2026-10-12
 * "no detail" decision): the row icon and the toolbar Edit (one ticked row) open it ready to edit, a row click
 * opens it read-only.
 */
export function GlossaryPage({ onHome }: Props) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const qc = useQueryClient();
  const { data: rows = [], isFetching, refetch } = useGlossary();
  const loadMs = useFetchMs(isFetching);
  const [search, setSearch] = useState('');
  // Page-level maximize (Payment Terms Setup pattern): hides the title + toolbar, keeps the search row with
  // Export + Restore so the grid fills the page; Esc restores.
  const [gridMax, setGridMax] = useState(false);
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setGridMax(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gridMax]);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // The rows in the order the grid shows them (sorted / filtered) — "Detail View" with nothing ticked opens the top one.
  const shownRef = useRef<{ id: string }[]>([]);
  // Batch inline edit of Definition. The changed values (id → new text) live in a ref, and the page
  // holds only HOW MANY rows are changed: that is all the Save / Cancel buttons need, and it does not
  // move while the user keeps typing in a cell that is already changed.
  const editsRef = useRef<Record<string, string>>({});
  const [dirtyCount, setDirtyCount] = useState(0);
  const [cellEpoch, setCellEpoch] = useState(0);   // bumped to re-seed every cell (after Save / Cancel changes)
  const [saving, setSaving] = useState(false);
  // Detail View: undefined = the list; a row = that record open, read-only ('view') or editable ('edit').
  const [detail, setDetail] = useState<GlossaryRow | undefined>(undefined);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const openDetail = (r: GlossaryRow, m: 'view' | 'edit') => { setDetail(r); setMode(m); };
  const editSelected = () => {
    if (selectedIds.size !== 1) return;
    const [id] = [...selectedIds];
    const r = rows.find((x) => x.id === id);
    if (r) openDetail(r, 'edit');
  };

  const orig = useMemo(() => {
    const m: Record<string, string> = {};
    for (const r of rows) m[r.id] = r.definition ?? '';
    return m;
  }, [rows]);
  const origRef = useRef(orig);
  origRef.current = orig;

  const setDef = useCallback((id: string, value: string) => {
    const edits = editsRef.current;
    if (value === (origRef.current[id] ?? '')) delete edits[id];   // back to original ⇒ no longer changed
    else edits[id] = value;
    // Same number ⇒ React skips the re-render. When it does move (first change in a cell), the page
    // update is marked low-priority so the letter just typed is painted first.
    const n = Object.keys(edits).length;
    startTransition(() => setDirtyCount(n));
  }, []);
  const clearEdits = () => { editsRef.current = {}; setDirtyCount(0); setCellEpoch((n) => n + 1); };

  const saveChanges = async () => {
    if (dirtyCount === 0) return;
    // Shared confirm-before-save + "Saved successfully." after (standard dialog
    // format, 2026-07-03 — the batch save was previously silent).
    if (!await confirm({ title: 'Save', message: 'Do you want to Save?' })) return;
    setSaving(true);
    try {
      await runLoading((async () => {
        for (const [id, definition] of Object.entries(editsRef.current)) {
          await api.put(`/api/v1/system/glossary/${id}/definition`, { definition: definition.trim() ? definition : null });
        }
      })(), { blocking: true, label: 'Saving changes…' });
      await qc.invalidateQueries({ queryKey: ['sm-glossary'] });
      clearEdits();
      notify.success('Saved successfully.');
    } catch (e) { notify.error(errMessage(e)); }
    finally { setSaving(false); }
  };
  const cancelChanges = async () => {
    if (dirtyCount === 0) return;
    if (!await confirm({ title: 'Cancel changes', message: `Discard ${dirtyCount} unsaved Definition change${dirtyCount === 1 ? '' : 's'}?` })) return;
    clearEdits();
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.fieldId, r.fieldName, r.acronym, r.definition, r.tableName, r.module, r.bo]
      .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  // LIMS shows the sort affordance ONLY on FieldID + FieldName (Code / Term) — those two are sortable;
  // Acronym / Definition / TableName / Module / BO are filter-only. Default sort = FieldID asc (Code SortOrder=Ascending).
  const columns = useMemo<AppColumn<GlossaryRow>[]>(() => [
    {
      // Row icon (G5): opens the record in the Detail View, ready to edit.
      field: 'detailedit', header: '', headerText: 'Edit', noExport: true, sortable: false, filterable: false, width: 44, align: 'center', text: () => '',
      render: (r) => (
        <button className="pts-icon-btn edit" title="Edit" aria-label={`Edit ${r.fieldName ?? 'glossary entry'}`}
          onClick={(e) => { e.stopPropagation(); setDetail(r); setMode('edit'); }}><i className="ti ti-edit" /></button>
      ),
    },
    { field: 'fieldId', header: 'FieldID', width: '8%', minWidth: 60, text: (r) => txt(r.fieldId),
      compare: (a, b) => txt(a.fieldId).localeCompare(txt(b.fieldId), undefined, { numeric: true }) },
    { field: 'fieldName', header: 'FieldName', width: '15%', minWidth: 130, text: (r) => txt(r.fieldName) },
    { field: 'acronym', header: 'Acronym', width: '13%', minWidth: 100, sortable: false, text: (r) => txt(r.acronym) },
    {
      field: 'definition', header: 'Definition', width: '17%', minWidth: 150, sortable: false, text: (r) => txt(r.definition),
      // The ONLY editable column (LIMS batch edit). Always-on input that reads like a cell until focused.
      // Empty Definition shows a blank cell (LIMS parity) — no placeholder dash.
      render: (r) => (
        <DefinitionCell key={`${r.id}:${cellEpoch}`} id={r.id} initial={editsRef.current[r.id] ?? r.definition ?? ''}
          original={r.definition ?? ''} onEdit={setDef} />
      ),
    },
    { field: 'tableName', header: 'TableName', width: '15%', minWidth: 120, sortable: false, text: (r) => txt(r.tableName) },
    { field: 'module', header: 'Module', width: '14%', minWidth: 110, sortable: false, text: (r) => txt(r.module) },
    { field: 'bo', header: 'BO', width: '18%', minWidth: 140, sortable: false, text: (r) => txt(r.bo) },
  ], [cellEpoch, setDef]);

  // Export rows carry the unsaved Definition text; rebuilt only when the list or the set of changed rows moves.
  const exportRows = useMemo(
    () => visible.map((r) => [txt(r.fieldId), txt(r.fieldName), txt(r.acronym), editsRef.current[r.id] ?? txt(r.definition), txt(r.tableName), txt(r.module), txt(r.bo)]),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dirtyCount / cellEpoch stand in for the ref's contents
    [visible, dirtyCount, cellEpoch],
  );

  // Batch-edit actions → keyboard shortcuts via the ONE shared hotkey layer
  // (standing rule 14). The call is unconditional (rules of hooks); the ARRAY is
  // gated to when there are unsaved edits, matching when the buttons render.
  // Esc uses allowInInput:false so it never discards edits while typing a cell.
  useHotkeys(dirtyCount > 0 && detail === undefined ? [
    { combo: 'mod+s', label: 'Save changes', group: 'Glossary',
      run: () => void saveChanges(), disabled: saving },
    { combo: 'esc', label: 'Cancel changes', group: 'Glossary',
      run: () => void cancelChanges(), disabled: saving, allowInInput: false },
  ] : []);

  // ── Full-page Detail View — replaces the list while open (standing rule 12). Keyed by the record so the
  //    prev / next arrows start each record afresh. ──
  if (detail !== undefined) {
    return (
      <GlossaryDetailView key={detail.id} row={detail} rows={visible} mode={mode} setMode={setMode}
        onOpen={(r) => setDetail(r)} onClose={() => setDetail(undefined)} onHome={onHome} />
    );
  }

  return (
    <div className={`acc-page${gridMax ? " acc-fullgrid" : ""}`}>
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Glossary</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        {/* Breadcrumb on top, then the List/Detail switch beside the FormTitle (as City / Payment Terms Setup, owner
            request 2026-10-15). "Detail View" opens the ticked record — or the first one — read-only. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <ViewToggle view="list" detailDisabled={rows.length === 0}
            onChange={(v) => {
              if (v !== 'detail') return;
              const id = selectedIds.size === 1 ? String([...selectedIds][0]) : (shownRef.current[0] ?? rows[0])?.id;
              const r = rows.find((x) => x.id === id); if (r) openDetail(r, 'view');
            }} />
          <FormTitle pageId="sm-glossary" loadMs={loadMs ?? null} loading={isFetching} auditFormName="Glossary" />
        </div>
      </div>
      <div className="acc-body acc-search-tight">
        {/* Action bar as G5: Edit · Refresh · Export to (Search on its own row below). Edit opens the ONE ticked
            record in the Detail View; Definition can still be typed straight into its cell and committed with
            Save changes under the grid. */}
        {!gridMax && (
        <AppToolbar
          onEdit={editSelected} editDisabled={selectedIds.size !== 1}
          onRefresh={() => void refetch()} refreshing={isFetching}
          // Export to on the action bar (owner request 2026-10-09); Search sits on the search row above the grid (2026-10-14).
          right={
            <>
              <ExportMenu triggerClassName="ca-btn" filename="Glossary"
                headers={['FieldID', 'FieldName', 'Acronym', 'Definition', 'TableName', 'Module', 'BO']}
                rows={exportRows} />
            </>
          }
        />
        )}
        {/* ONE white grid card — grid + its single footer only. */}
        {/* Search + Export + Maximize on ONE row above the grid card (Payment Terms Setup / ModuleListView layout,
            owner request 2026-10-14) — stays visible while maximized, so Restore is always reachable. */}
        <div className="list-search-row">
          <div className="list-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch("")}>✕</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="Glossary" {...gridExportMatrix(visible, columns)} />
            <button type="button" className={`dv-gridact${gridMax ? " dv-on" : ""}`} onClick={() => setGridMax((v) => !v)}
              title={gridMax ? "Restore" : "Maximize"}>
              <i className={`ti ti-arrows-${gridMax ? "minimize" : "maximize"}`} />
            </button>
          </div>
        </div>
        <div className="acc-card">
          <AppDataGrid<GlossaryRow> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
            onVisibleRowsChange={(r) => { shownRef.current = r; }}
            onRowClick={(r) => openDetail(r, 'view')}
            initialSort={{ key: 'fieldId', dir: 'asc' }} loading={isFetching} emptyText="No glossary entries found." hideHeaderTools />
          {/* Batch-edit bar under the grid, right-aligned, as legacy: live once a Definition is changed. */}
          <div className="gloss-batchbar">
            <button className="ca-btn gloss-save" title={`Save changes (${formatCombo('mod+s')})`} onClick={() => void saveChanges()} disabled={saving || dirtyCount === 0}>
              <span className="tb-i">💾</span> Save changes{dirtyCount > 1 ? ` (${dirtyCount})` : ''}
            </button>
            <button className="ca-btn" title={`Cancel changes (${formatCombo('esc')})`} onClick={() => void cancelChanges()} disabled={saving || dirtyCount === 0}>
              <span className="tb-i">↩︎</span> Cancel changes
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}
